import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../utils/AuthContext';
import { useLanguage } from '../utils/i18n';
import { useCurrency } from '../utils/useCurrency';
import Logo from '../components/Logo';
import LanguageToggle from '../components/LanguageToggle';
import {
    Check,
    ChevronRight,
    Trash2,
    AlertCircle,
    UserX,
    Zap,
    Smartphone,
    Palette,
    BarChart3,
    Star,
    Layout,
    Scan,
    UserPlus,
    Gift,
    ArrowRight,
    Menu,
    X,
    Trophy,
    Clock,
    Bell,
    Send,
    ChevronDown
} from 'lucide-react';
import ROICalculator from '../components/ROICalculator';

export default function LandingSalons() {
    const { session, isEmailConfirmed } = useAuth();
    const { t } = useLanguage();
    const [billingCycle, setBillingCycle] = useState('monthly');
    const [scrolled, setScrolled] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [openFaq, setOpenFaq] = useState(null);
    const currencySymbol = useCurrency();

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToPricing = () => {
        document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
    };

    return (
        <div className="landing-page">
            {/* 1. ANNOUNCEMENT BAR */}
            <div className="announcement-bar">
                <span className="highlight" style={{ fontWeight: '700' }}>{t('salon_urgency_banner')}</span>
            </div>

            {/* 2. NAV */}
            <nav className={`landing-nav ${scrolled ? 'scrolled' : ''}`}>
                <div className="container nav-container">
                    <div className="nav-logo">
                        <Logo size={32} color="#2563EB" />
                        <span className="logo-text">Wafiyo</span>
                    </div>

                    <div className="nav-links desktop-only">
                        <a href="#features" className="nav-link">{t('features')}</a>
                        <a href="#how-it-works" className="nav-link">{t('how_it_works')}</a>
                        <a href="#pricing" className="nav-link">{t('choose_your_plan')}</a>
                    </div>

                    <div className="nav-actions">
                        <div className="desktop-only" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <LanguageToggle />
                            {!session && (
                                <Link to="/login" className="btn btn-ghost">{t('sign_in')}</Link>
                            )}
                            <Link to={(session && isEmailConfirmed) ? "/dashboard" : "/register"} className="btn btn-primary">
                                {(session && isEmailConfirmed) ? t('dashboard') : t('create_account')}
                            </Link>
                        </div>

                        <button
                            className="mobile-menu-toggle"
                            onClick={() => setIsMenuOpen(!isMenuOpen)}
                            aria-label="Toggle menu"
                        >
                            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
                        </button>
                    </div>
                </div>

                {/* Mobile Dropdown */}
                {isMenuOpen && (
                    <div className="mobile-dropdown animate-zoom">
                        <div className="mobile-nav-links">
                            <a href="#features" className="mobile-nav-link" onClick={() => setIsMenuOpen(false)}>{t('features')}</a>
                            <a href="#how-it-works" className="mobile-nav-link" onClick={() => setIsMenuOpen(false)}>{t('how_it_works')}</a>
                            <a href="#pricing" className="mobile-nav-link" onClick={() => setIsMenuOpen(false)}>{t('choose_your_plan')}</a>
                            <hr style={{ border: 'none', borderTop: '1px solid rgba(0,0,0,0.08)', margin: '16px 0' }} />
                            <div className="mobile-nav-actions">
                                <div className="mobile-lang-wrapper">
                                    <LanguageToggle />
                                </div>
                                {!session && (
                                    <Link to="/login" className="btn btn-outline" onClick={() => setIsMenuOpen(false)}>
                                        {t('sign_in')}
                                    </Link>
                                )}
                                <Link to={(session && isEmailConfirmed) ? "/dashboard" : "/register"} className="btn btn-primary btn-glow" onClick={() => setIsMenuOpen(false)}>
                                    {(session && isEmailConfirmed) ? t('dashboard') : t('create_account')}
                                </Link>
                            </div>
                        </div>
                    </div>
                )}
            </nav>

            {/* 3. HERO */}
            <header className="hero-section" style={{ minHeight: 'auto' }}>
                <div className="container">
                    <div className="zigzag-row">
                        <div className="zigzag-content" style={{ textAlign: 'left' }}>
                            <h1 className="hero-title" style={{ fontSize: '56px', textAlign: 'left', marginBottom: '16px', color: '#0A0A0A' }}>
                                {t('salon_hero_h1')}
                            </h1>
                            <p className="hero-subtitle" style={{ fontSize: '18px', textAlign: 'left', marginLeft: '0', maxWidth: '100%', marginBottom: '12px', color: '#555' }}>
                                {t('salon_hero_sub')}
                            </p>
                            <p className="hero-italic-text" style={{ fontSize: '16px', textAlign: 'left', marginBottom: '32px', color: '#777', fontStyle: 'italic' }}>
                                Sin papel. Sin apps. Listo en 5 minutos.
                            </p>
                            <div className="hero-actions" style={{ justifyContent: 'flex-start', marginBottom: '24px' }}>
                                <Link to="/dashboard?demo=true" className="btn btn-primary btn-lg" style={{ padding: '18px 36px', fontSize: '17px', background: '#1A7A4A', textDecoration: 'none' }}>{t('cta_free')}</Link>
                                <Link to="/register" className="btn btn-soft-primary btn-lg mobile-only">
                                    {t('create_account')}
                                </Link>
                                <button className="btn btn-outline btn-lg" onClick={scrollToPricing} style={{ color: '#0A0A0A', borderColor: 'rgba(0,0,0,0.15)' }}>
                                    {t('cta_demo')}
                                </button>
                            </div>
                            <div className="trust-micro-copy" style={{ color: '#777', fontSize: '14px', display: 'flex', gap: '20px' }}>
                                <span>✓ {t('trust_1')}</span>
                                <span>✓ {t('trust_2')}</span>
                                <span>✓ {t('trust_3')}</span>
                            </div>

                            {/* Metrics moved into hero */}
                            <div className="metrics-container" style={{ display: 'flex', gap: '32px', marginTop: '40px', flexWrap: 'wrap' }}>
                                <div>
                                    <span style={{ fontSize: '36px', fontWeight: '800', color: '#1A7A4A', display: 'block' }}>{t('metric_1_val')}</span>
                                    <span style={{ fontSize: '13px', color: '#777', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('metric_1_label')}</span>
                                </div>
                                <div>
                                    <span style={{ fontSize: '36px', fontWeight: '800', color: '#1A7A4A', display: 'block' }}>{t('metric_2_val')}</span>
                                    <span style={{ fontSize: '13px', color: '#777', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('metric_2_label')}</span>
                                </div>
                                <div>
                                    <span style={{ fontSize: '36px', fontWeight: '800', color: '#1A7A4A', display: 'block' }}>{t('metric_3_val')}</span>
                                    <span style={{ fontSize: '13px', color: '#777', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('metric_3_label')}</span>
                                </div>
                            </div>
                        </div>

                        <div className="zigzag-visual" style={{
                            background: 'transparent',
                            border: 'none',
                            boxShadow: 'none',
                            aspectRatio: 'unset',
                            overflow: 'visible',
                            height: 'auto',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            {/* Interactive visual container */}
                            <div style={{
                                background: '#f8f9fa',
                                borderRadius: '20px',
                                border: '1px solid #ececec',
                                padding: '24px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '12px',
                                width: '100%',
                                maxWidth: '600px'
                            }}>
                                {/* PHONE MOCKUP */}
                                <div style={{ display: 'flex', justifyContent: 'center' }}>
                                    <img
                                        src="/salon-mockup.png"
                                        alt="Wafiyo Loyalty Card Preview"
                                        className="hero-mockup-img"
                                        loading="eager"
                                    />
                                </div>

                                {/* ELEMENTO 1 — WALLET BUTTONS */}
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    {/* Apple Wallet Button */}
                                    <button style={{
                                        flex: 1,
                                        background: '#fff',
                                        border: '1px solid #e0e0e0',
                                        borderRadius: '10px',
                                        padding: '10px 12px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        cursor: 'pointer',
                                        fontSize: '12px',
                                        fontWeight: 500,
                                        color: '#333'
                                    }}>
                                        <span style={{
                                            width: '20px',
                                            height: '20px',
                                            background: '#000',
                                            borderRadius: '4px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0
                                        }}>
                                            <svg width="12" height="15" viewBox="0 0 170 170" fill="none">
                                                <path d="M150.37 130.25c-2.45 5.66-5.35 10.87-8.71 15.66-4.58 6.53-8.33 11.05-11.22 13.56-4.48 4.12-9.28 6.23-14.42 6.35-3.69 0-8.14-1.05-13.32-3.18-5.2-2.12-9.97-3.17-14.34-3.17-4.58 0-9.49 1.05-14.75 3.17-5.28 2.13-9.54 3.25-12.8 3.35-4.93.21-9.84-1.96-14.75-6.52-3.13-2.73-7.05-7.41-11.76-14.04-5.05-7.14-9.2-15.42-12.45-24.87-3.47-10.2-5.21-20.07-5.21-29.59 0-10.95 2.36-20.4 7.09-28.32 3.71-6.37 8.65-11.4 14.83-15.11 6.18-3.71 12.86-5.6 20.05-5.73 3.92 0 9.07 1.21 15.47 3.6 6.38 2.4 10.48 3.61 12.28 3.61 1.34 0 5.87-1.42 13.57-4.24 7.28-2.61 13.42-3.7 18.45-3.28 13.63 1.1 23.87 6.47 30.68 16.15-12.2 7.39-18.22 17.73-18.09 31 .13 10.33 3.86 18.93 11.16 25.77 3.32 3.15 7.03 5.58 11.15 7.31-.89 2.6-1.84 5.09-2.84 7.48zM119.04 7.01c0 8.1-2.96 15.66-8.86 22.67-7.12 8.32-15.73 13.13-25.07 12.37a25.2 25.2 0 0 1-.19-3.07c0-7.77 3.38-16.08 9.39-22.89 3-3.45 6.81-6.31 11.43-8.59 4.61-2.25 8.97-3.49 13.07-3.73.13 1.09.19 2.18.19 3.24h.04z" fill="white" />
                                            </svg>
                                        </span>
                                        Apple Wallet
                                    </button>
                                    {/* Google Wallet Button */}
                                    <button style={{
                                        flex: 1,
                                        background: '#fff',

                                        border: '1px solid #e0e0e0',
                                        borderRadius: '10px',
                                        padding: '10px 12px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        cursor: 'pointer',
                                        fontSize: '12px',
                                        fontWeight: 500,
                                        color: '#333'
                                    }}>
                                        <svg width="20" height="20" viewBox="0 0 48 48" fill="none">
                                            <path d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" fill="#FFC107" />
                                            <path d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" fill="#FF3D00" />
                                            <path d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" fill="#4CAF50" />
                                            <path d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" fill="#1976D2" />
                                        </svg>
                                        Google Wallet
                                    </button>
                                </div>

                                {/* ELEMENTO 2 — NOTIFICACIÓN PUSH */}
                                <div style={{
                                    background: '#fff',
                                    border: '1px solid #e8e8e8',
                                    borderRadius: '12px',
                                    padding: '12px 14px',
                                    display: 'flex',
                                    gap: '12px',
                                    alignItems: 'flex-start'
                                }}>
                                    <div style={{
                                        width: '32px',
                                        height: '32px',
                                        background: '#f0fdf4',
                                        borderRadius: '8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0
                                    }}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                                            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                                        </svg>
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <p style={{ margin: 0, fontSize: '13px', lineHeight: '1.5', color: '#444' }}>
                                            <span style={{ fontWeight: 600, color: '#0f0f0f' }}>{t('salon_hero_push_bold')}</span>{' '}
                                            {t('salon_hero_push_body')}
                                        </p>
                                        <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#aaa' }}>
                                            {t('hero_push_meta')}
                                        </p>
                                    </div>
                                </div>

                                {/* ELEMENTO 3 — SOCIAL PROOF */}
                                <div style={{
                                    background: '#fff',
                                    border: '1px solid #e8e8e8',
                                    borderRadius: '12px',
                                    padding: '14px 16px'
                                }}>
                                    {/* Avatares solapados */}
                                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                                        {[
                                            { initial: 'C', bg: '#fde68a', color: '#92400e' },
                                            { initial: 'M', bg: '#bfdbfe', color: '#1e40af' },
                                            { initial: 'R', bg: '#d9f99d', color: '#3f6212' },
                                            { initial: 'A', bg: '#fecaca', color: '#991b1b' }
                                        ].map((avatar, i) => (
                                            <div key={avatar.initial} style={{
                                                width: '26px',
                                                height: '26px',
                                                borderRadius: '50%',
                                                border: '2px solid #fff',
                                                background: avatar.bg,
                                                color: avatar.color,
                                                fontSize: '11px',
                                                fontWeight: 700,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                marginLeft: i === 0 ? '0' : '-6px',
                                                position: 'relative',
                                                zIndex: 4 - i
                                            }}>
                                                {avatar.initial}
                                            </div>
                                        ))}
                                    </div>
                                    <p style={{ margin: 0, fontSize: '13px', color: '#444', lineHeight: '1.4' }}>
                                        <span style={{ fontWeight: 600 }}>{t('salon_hero_social_proof')}</span> {t('hero_social_proof_suffix')}
                                    </p>
                                    <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#f59e0b', letterSpacing: '1px' }}>
                                        ★★★★★
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {/* 4. SOCIAL PROOF BAR */}
            <div className="social-proof-bar" style={{ padding: '40px 0', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)', textAlign: 'center' }}>
                <p style={{ color: '#777', fontSize: '14px', marginBottom: '24px' }}>{t('salon_social_proof')}</p>
                <div className="container social-proof-container">
                    <span style={{ fontWeight: '700', fontSize: '18px', color: '#0A0A0A' }}>{t('salon_sp_1')}</span>
                    <span style={{ fontWeight: '700', fontSize: '18px', color: '#0A0A0A' }}>{t('salon_sp_2')}</span>
                    <span style={{ fontWeight: '700', fontSize: '18px', color: '#0A0A0A' }}>{t('salon_sp_3')}</span>
                    <span style={{ fontWeight: '700', fontSize: '18px', color: '#0A0A0A' }}>{t('salon_sp_4')}</span>
                </div>
            </div>

            {/* 5. PROBLEM SECTION */}
            <section className="problem-section" style={{ textAlign: 'center' }}>
                <div className="container">
                    <h2 className="section-title" style={{ fontSize: '36px', marginBottom: '48px', color: '#0A0A0A' }}>{t('problem_h2')}</h2>
                    <div className="steps-grid">
                        <div className="step-card glass-card">
                            <Trash2 size={40} color="#ef4444" style={{ marginBottom: '20px' }} />
                            <h3 className="step-title" style={{ fontSize: '18px' }}>{t('salon_pain_1_title')}</h3>
                            <p className="step-desc">{t('salon_pain_1_desc')}</p>
                        </div>
                        <div className="step-card glass-card">
                            <UserX size={40} color="#ef4444" style={{ marginBottom: '20px' }} />
                            <h3 className="step-title" style={{ fontSize: '18px' }}>{t('salon_pain_2_title')}</h3>
                            <p className="step-desc">{t('salon_pain_2_desc')}</p>
                        </div>
                        <div className="step-card glass-card">
                            <AlertCircle size={40} color="#ef4444" style={{ marginBottom: '20px' }} />
                            <h3 className="step-title" style={{ fontSize: '18px' }}>{t('salon_pain_3_title')}</h3>
                            <p className="step-desc">{t('salon_pain_3_desc')}</p>
                        </div>
                    </div>
                    <p style={{ marginTop: '48px', color: '#1A7A4A', fontWeight: '600' }}>{t('transition_text')}</p>
                </div>
            </section>

            {/* 6. HOW IT WORKS */}
            <section id="how-it-works" className="how-it-works-section" style={{ background: '#F5F5F5' }}>
                <div className="container">
                    <h2 className="section-title" style={{ fontSize: '36px', marginBottom: '64px', color: '#0A0A0A' }}>{t('how_it_works_h2')}</h2>
                    <div className="steps-container">
                        <div className="step-item" style={{ position: 'relative', zIndex: '1', textAlign: 'center' }}>
                            <div style={{ width: '80px', height: '80px', background: '#FFFFFF', border: '1px solid rgba(0,0,0,0.08)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                                <UserPlus size={32} color="#1A7A4A" />
                            </div>
                            <h3 style={{ fontSize: '18px', marginBottom: '12px', color: '#0A0A0A' }}>{t('step_1_title')}</h3>
                            <p style={{ color: '#555' }}>{t('step_1_desc')}</p>
                        </div>
                        <div className="step-item" style={{ position: 'relative', zIndex: '1', textAlign: 'center' }}>
                            <div style={{ width: '80px', height: '80px', background: '#FFFFFF', border: '1px solid rgba(0,0,0,0.08)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                                <Smartphone size={32} color="#1A7A4A" />
                            </div>
                            <h3 style={{ fontSize: '18px', marginBottom: '12px', color: '#0A0A0A' }}>{t('step_2_title')}</h3>
                            <p style={{ color: '#555' }}>{t('step_2_desc')}</p>
                        </div>
                        <div className="step-item" style={{ position: 'relative', zIndex: '1', textAlign: 'center' }}>
                            <div style={{ width: '80px', height: '80px', background: '#FFFFFF', border: '1px solid rgba(0,0,0,0.08)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                                <Scan size={32} color="#1A7A4A" />
                            </div>
                            <h3 style={{ fontSize: '18px', marginBottom: '12px', color: '#0A0A0A' }}>{t('step_3_title')}</h3>
                            <p style={{ color: '#555' }}>{t('step_3_desc')}</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* 7. FEATURES (Zigzag) */}
            <section id="features" className="zigzag-section">
                <div className="container">
                    <div className="zigzag-row">
                        <div className="zigzag-content">
                            <h2 style={{ fontSize: '36px', marginBottom: '20px', color: '#0A0A0A' }}>{t('salon_feat_1_title')}</h2>
                            <p style={{ color: '#555', fontSize: '18px', lineHeight: '1.6' }}>{t('salon_feat_1_desc')}</p>
                        </div>
                        <div className="zigzag-visual">
                            <div style={{ width: '80%', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                                {/* Mock notification card */}
                                <div style={{ background: 'rgba(26,122,74,0.06)', border: '1px solid rgba(26,122,74,0.15)', borderRadius: '16px', padding: '20px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                                        <Bell size={18} color="#1A7A4A" />
                                        <span style={{ fontSize: 13, fontWeight: 700, color: '#0A0A0A' }}>{t('notif_title')}</span>
                                    </div>
                                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                        <div style={{ flex: 1, height: 38, background: 'rgba(0,0,0,0.03)', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 10, display: 'flex', alignItems: 'center', padding: '0 12px' }}>
                                            <span style={{ fontSize: 12, color: '#777' }}>{t('salon_feat_5_mock_msg')}</span>
                                        </div>
                                        <div style={{ width: 38, height: 38, background: '#1A7A4A', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                            <Send size={14} color="#fff" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="zigzag-row reverse">
                        <div className="zigzag-content">
                            <h2 style={{ fontSize: '36px', marginBottom: '20px', color: '#0A0A0A' }}>{t('salon_feat_2_title')}</h2>
                            <p style={{ color: '#555', fontSize: '18px', lineHeight: '1.6' }}>{t('salon_feat_2_desc')}</p>
                        </div>
                        <div className="zigzag-visual">
                            <div style={{ width: '70%', height: '80%', background: '#FFFFFF', borderRadius: '24px', border: '1px solid rgba(0,0,0,0.08)', position: 'relative', boxShadow: '0 4px 16px rgba(0,0,0,0.06)' }}>
                                <div style={{ height: '40px', borderBottom: '1px solid rgba(0,0,0,0.06)' }}></div>
                                <div style={{ padding: '20px' }}>
                                    <div style={{ height: '100px', background: '#1A7A4A', borderRadius: '12px', marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Check color="white" size={40} />
                                    </div>
                                    <div style={{ height: '20px', background: '#e8e8e8', borderRadius: '4px', width: '60%', marginBottom: '12px' }}></div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="zigzag-row">
                        <div className="zigzag-content">
                            <h2 style={{ fontSize: '36px', marginBottom: '20px', color: '#0A0A0A' }}>{t('salon_feat_3_title')}</h2>
                            <p style={{ color: '#555', fontSize: '18px', lineHeight: '1.6' }}>{t('salon_feat_3_desc')}</p>
                        </div>
                        <div className="zigzag-visual">
                            {/* Mini analytics chart mockup */}
                            <div style={{ width: '75%', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '6px', height: '120px', justifyContent: 'center' }}>
                                    {[40, 60, 85, 70, 95, 75, 50].map((h, i) => (
                                        <div key={i} style={{ width: '14%', height: `${h}%`, background: 'linear-gradient(180deg, #8b5cf6, #6366f1)', borderRadius: '4px 4px 0 0', transition: 'height 0.3s' }} />
                                    ))}
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0 2px' }}>
                                    {['L', 'M', 'X', 'J', 'V', 'S', 'D'].map(d => (
                                        <span key={d} style={{ fontSize: '10px', color: 'var(--text-dim)', fontWeight: 600 }}>{d}</span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="zigzag-row reverse">
                        <div className="zigzag-content">
                            <h2 style={{ fontSize: '36px', marginBottom: '20px', color: '#0A0A0A' }}>{t('salon_feat_4_title')}</h2>
                            <p style={{ color: '#555', fontSize: '18px', lineHeight: '1.6' }}>{t('salon_feat_4_desc')}</p>
                        </div>
                        <div className="zigzag-visual">
                            <Palette size={80} color="#1A7A4A" />
                        </div>
                    </div>
                </div>
            </section>

            {/* TESTIMONIALS — moved directly after hero */}
            <section className="testimonials-section" style={{ background: '#F5F5F5' }}>
                <div className="container" style={{ textAlign: 'center' }}>
                    <h2 className="section-title" style={{ fontSize: '36px', marginBottom: '48px', color: '#0A0A0A' }}>{t('testi_h2')}</h2>
                    <div className="steps-grid">
                        {/* Testimonial 1 */}
                        <div className="step-card glass-card" style={{ textAlign: 'left' }}>
                            <div style={{ display: 'flex', gap: '4px', marginBottom: '12px' }}>
                                {[1, 2, 3, 4, 5].map(s => <Star key={s} size={20} fill="#fbbf24" color="#fbbf24" />)}
                            </div>
                            <p style={{ fontSize: '17px', fontStyle: 'italic', marginBottom: '20px', lineHeight: '1.6', color: '#0A0A0A' }}>
                                "{t('salon_testi_1_quote')}"
                            </p>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div style={{ width: '8px', height: '8px', background: '#1A7A4A', borderRadius: '50%' }}></div>
                                <p style={{ fontSize: '12px', color: '#777', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    {t('salon_testi_1_type')}
                                </p>
                            </div>
                        </div>

                        {/* Testimonial 2 */}
                        <div className="step-card glass-card" style={{ textAlign: 'left' }}>
                            <div style={{ display: 'flex', gap: '4px', marginBottom: '12px' }}>
                                {[1, 2, 3, 4, 5].map(s => <Star key={s} size={20} fill="#fbbf24" color="#fbbf24" />)}
                            </div>
                            <p style={{ fontSize: '17px', fontStyle: 'italic', marginBottom: '20px', lineHeight: '1.6', color: '#0A0A0A' }}>
                                "{t('salon_testi_2_quote')}"
                            </p>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div style={{ width: '8px', height: '8px', background: '#1A7A4A', borderRadius: '50%' }}></div>
                                <p style={{ fontSize: '12px', color: '#777', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    {t('salon_testi_2_type')}
                                </p>
                            </div>
                        </div>

                        {/* Testimonial 3 */}
                        <div className="step-card glass-card" style={{ textAlign: 'left' }}>
                            <div style={{ display: 'flex', gap: '4px', marginBottom: '12px' }}>
                                {[1, 2, 3, 4, 5].map(s => <Star key={s} size={20} fill="#fbbf24" color="#fbbf24" />)}
                            </div>
                            <p style={{ fontSize: '17px', fontStyle: 'italic', marginBottom: '20px', lineHeight: '1.6', color: '#0A0A0A' }}>
                                "{t('salon_testi_3_quote')}"
                            </p>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div style={{ width: '8px', height: '8px', background: '#1A7A4A', borderRadius: '50%' }}></div>
                                <p style={{ fontSize: '12px', color: '#777', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    {t('salon_testi_3_type')}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>


            {/* 9. ROI CALCULATOR */}
            <section className="roi-section" style={{ padding: '0px 20px 80px', background: '#fff' }}>
                <div className="container" style={{ maxWidth: '900px' }}>
                    <h2 className="section-title" style={{ fontSize: '36px', marginBottom: '32px', color: '#0A0A0A', textAlign: 'center' }}>
                        {t('roi_section_title')}
                    </h2>
                    <ROICalculator defaultCustomers={20} defaultSpend={40} />
                </div>
            </section>

            {/* 10. PRICING */}
            <section id="pricing" className="pricing-section" style={{ background: '#F5F5F5' }}>
                <div className="container" style={{ textAlign: 'center' }}>
                    <h2 className="section-title" style={{ fontSize: '36px', marginBottom: '16px', color: '#0A0A0A' }}>{t('choose_your_plan')}</h2>
                    <p style={{ color: '#555', marginBottom: '40px' }}>{t('pricing_subtitle')}</p>


                    <div className="steps-grid" style={{ maxWidth: '900px', margin: '0 auto' }}>
                        {/* Monthly */}
                        <div className={`step-card glass-card ${billingCycle === 'monthly' ? 'active' : ''}`} style={{ borderColor: billingCycle === 'monthly' ? '#1A7A4A' : 'rgba(0,0,0,0.08)', position: 'relative', overflow: 'visible' }}>
                            <div style={{
                                position: 'absolute',
                                top: -16,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                background: 'linear-gradient(135deg, #10b981, #059669)',
                                padding: '6px 16px',
                                borderRadius: 20,
                                fontSize: 12,
                                fontWeight: 800,
                                color: '#fff',
                                textTransform: 'uppercase',
                                letterSpacing: 0.5,
                                whiteSpace: 'nowrap',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                                boxShadow: '0 4px 12px rgba(16,185,129,0.3)',
                                zIndex: 2
                            }}>
                                🎁 {t('free_trial_badge')}
                            </div>
                            <h3 style={{ fontSize: '20px', marginBottom: '12px', marginTop: 12, color: '#0A0A0A' }}>{t('monthly_plan')}</h3>
                            <div style={{ marginBottom: '24px' }}>
                                <span style={{ fontSize: '40px', fontWeight: '800', color: '#0A0A0A' }}>{currencySymbol}0</span>
                                <span style={{ color: '#555', fontSize: '18px' }}> / {t('first_month')}</span>
                                <div
                                    style={{ fontSize: '14px', color: '#777', marginTop: '6px', fontWeight: 500 }}
                                    dangerouslySetInnerHTML={{ __html: t('then_price', { symbol: currencySymbol, amount: `9<span style="font-size: 0.75em;">.99</span>` }) }}
                                />
                            </div>
                            <ul style={{
                                listStyle: 'none',
                                padding: 0,
                                marginBottom: '32px',
                                textAlign: 'left',
                                width: 'fit-content',
                                margin: '0 auto 32px'
                            }}>
                                <li style={{ marginBottom: '12px', fontSize: '14px', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                                    <Check size={16} color="#1A7A4A" style={{ marginTop: 2, flexShrink: 0 }} />
                                    <span style={{ lineHeight: '1.4', color: '#333' }}>{t('unlimited_clients')}</span>
                                </li>
                                <li style={{ marginBottom: '12px', fontSize: '14px', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                                    <Check size={16} color="#1A7A4A" style={{ marginTop: 2, flexShrink: 0 }} />
                                    <span style={{ lineHeight: '1.4', color: '#333' }}>{t('qr_code_scanning')}</span>
                                </li>
                                <li style={{ marginBottom: '12px', fontSize: '14px', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                                    <Check size={16} color="#1A7A4A" style={{ marginTop: 2, flexShrink: 0 }} />
                                    <span style={{ lineHeight: '1.4', color: '#333' }}>{t('google_wallet_integration')}</span>
                                </li>
                                <li style={{ marginBottom: '12px', fontSize: '14px', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                                    <Check size={16} color="#1A7A4A" style={{ marginTop: 2, flexShrink: 0 }} />
                                    <span style={{ lineHeight: '1.4', color: '#333' }}>{t('push_notifications_feature')}</span>
                                </li>
                            </ul>
                            <Link to="/register" className="btn btn-primary" style={{ width: '100%' }}>{t('cta_monthly')}</Link>
                        </div>

                        {/* Yearly */}
                        <div className={`step-card glass-card featured-plan ${billingCycle === 'yearly' ? 'active' : ''}`} style={{ borderColor: '#1A7A4A', position: 'relative', background: 'rgba(26,122,74,0.04)', boxShadow: '0 0 20px rgba(26,122,74,0.08)', overflow: 'visible' }}>
                            <div style={{
                                position: 'absolute',
                                top: -16,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                background: '#1A7A4A',
                                padding: '6px 16px',
                                borderRadius: 20,
                                fontSize: 11,
                                fontWeight: '800',
                                color: '#fff',
                                textTransform: 'uppercase',
                                letterSpacing: 0.5,
                                whiteSpace: 'nowrap',
                                boxShadow: '0 4px 12px rgba(26,122,74,0.2)',
                                zIndex: 2
                            }}>
                                {t('popular_badge')}
                            </div>
                            <h3 style={{ fontSize: '20px', marginBottom: '12px', marginTop: 12, color: '#0A0A0A' }}>{t('yearly_plan')}</h3>
                            <div style={{ marginBottom: '24px' }}>
                                <span style={{ fontSize: '40px', fontWeight: '800', color: '#0A0A0A' }}>{currencySymbol}89<span style={{ fontSize: '0.6em' }}>.99</span></span>
                                <span style={{ color: '#555' }}>/{t('year')}</span>
                            </div>
                            <ul style={{
                                listStyle: 'none',
                                padding: 0,
                                marginBottom: '32px',
                                textAlign: 'left',
                                width: 'fit-content',
                                margin: '0 auto 32px'
                            }}>
                                <li style={{ marginBottom: '12px', fontSize: '14px', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                                    <Check size={16} color="#1A7A4A" style={{ marginTop: 2, flexShrink: 0 }} />
                                    <span style={{ lineHeight: '1.4', color: '#333' }}>{t('all_monthly_features')}</span>
                                </li>
                                <li style={{ marginBottom: '12px', fontSize: '14px', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                                    <Check size={16} color="#1A7A4A" style={{ marginTop: 2, flexShrink: 0 }} />
                                    <span style={{ lineHeight: '1.4', color: '#333' }}>{t('three_months_free')}</span>
                                </li>
                                <li style={{ marginBottom: '12px', fontSize: '14px', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                                    <Check size={16} color="#1A7A4A" style={{ marginTop: 2, flexShrink: 0 }} />
                                    <span style={{ lineHeight: '1.4', color: '#333' }}>{t('priority_support')}</span>
                                </li>
                            </ul>
                            <Link to="/register" className="btn btn-primary btn-glow" style={{ width: '100%' }}>{t('cta_yearly')}</Link>
                        </div>
                    </div>
                    <p style={{ marginTop: '32px', color: '#555', fontSize: '14px' }}>{t('cancel_anytime')}</p>

                    {/* FAQ ACCORDION */}
                    <div style={{ maxWidth: '700px', margin: '64px auto 0', textAlign: 'left' }}>
                        <h3 style={{ fontSize: '24px', fontWeight: 700, color: '#0A0A0A', textAlign: 'center', marginBottom: '32px' }}>{t('faq_title')}</h3>
                        {[1, 2, 3, 4, 5].map(n => {
                            const isOpen = openFaq === n;
                            return (
                                <div key={n} style={{
                                    borderBottom: '1px solid rgba(0,0,0,0.08)',
                                    overflow: 'hidden'
                                }}>
                                    <button
                                        onClick={() => setOpenFaq(isOpen ? null : n)}
                                        style={{
                                            width: '100%',
                                            padding: '18px 0',
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            gap: '16px',
                                            textAlign: 'left'
                                        }}
                                    >
                                        <span style={{ fontSize: '15px', fontWeight: 600, color: '#0A0A0A', lineHeight: '1.4' }}>
                                            {t(`faq_q${n}`)}
                                        </span>
                                        <ChevronDown
                                            size={18}
                                            color="#777"
                                            style={{
                                                flexShrink: 0,
                                                transition: 'transform 0.25s ease',
                                                transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                                            }}
                                        />
                                    </button>
                                    <div style={{
                                        maxHeight: isOpen ? '200px' : '0',
                                        transition: 'max-height 0.3s ease, padding 0.3s ease',
                                        paddingBottom: isOpen ? '18px' : '0',
                                        overflow: 'hidden'
                                    }}>
                                        <p style={{ margin: 0, fontSize: '14px', color: '#555', lineHeight: '1.6' }}>
                                            {t(`faq_a${n}`)}
                                        </p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* 11. FINAL CTA */}
            <section className="final-cta-section" style={{ textAlign: 'center', padding: '120px 20px' }}>
                <div className="container" style={{ maxWidth: '800px' }}>
                    <h2 className="final-cta-title" style={{ fontWeight: '800', marginBottom: '20px', color: '#0A0A0A' }}>{t('final_cta_h2')}</h2>
                    <p style={{ fontSize: '18px', color: '#555', marginBottom: '40px' }}>{t('final_cta_sub')}</p>
                    <Link to="/register" className="btn btn-primary btn-glow" style={{ padding: '16px 48px', fontSize: '18px' }}>
                        {t('create_account')}
                    </Link>
                    <p style={{ marginTop: '24px', fontSize: '14px', color: '#777' }}>{t('final_cta_micro')}</p>
                </div>
            </section>

            {/* 11.5. STICKY MOBILE CTA */}
            {scrolled && (
                <div className="sticky-mobile-cta">
                    <Link to="/register" className="btn btn-primary btn-glow" style={{ width: '100%', justifyContent: 'center', fontSize: '16px', padding: '16px' }}>
                        {t('start_free_trial', 'Empieza Gratis Ahora')}
                    </Link>
                </div>
            )}

            {/* 12. FOOTER */}
            <footer className="landing-footer" style={{ borderTop: '1px solid rgba(0,0,0,0.08)', background: '#F5F5F5' }}>
                <div className="container">
                    <div className="footer-content">
                        <div className="footer-brand">
                            <Logo size={32} />
                            <span className="logo-text" style={{ fontSize: '24px' }}>Wafiyo</span>
                            <p className="footer-tagline">{t('footer_tagline')}</p>
                        </div>
                        <div className="footer-links">
                            <div className="link-col">
                                <h4>{t('product')}</h4>
                                <a href="#features">{t('features')}</a>
                                <a href="#how-it-works">{t('how_it_works')}</a>
                                <Link to="/pricing">{t('choose_your_plan')}</Link>
                            </div>
                            <div className="link-col">
                                <h4>{t('company')}</h4>
                                <Link to="/about">{t('about_us')}</Link>
                                <Link to="/contact">{t('contact')}</Link>
                            </div>
                            <div className="link-col">
                                <h4>{t('legal')}</h4>
                                <Link to="/privacy">{t('privacy_policy_title')}</Link>
                                <Link to="/terms">{t('tos_title')}</Link>
                                <Link to="/refund">{t('refund_policy_title')}</Link>
                            </div>
                        </div>
                    </div>
                    <div className="footer-bottom" style={{ borderTop: '1px solid var(--border)', paddingTop: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <p style={{ fontSize: '14px', color: 'var(--text-dim)' }}>© {new Date().getFullYear()} Wafiyo. {t('all_rights_reserved')}</p>
                        <div style={{ display: 'flex', gap: '24px' }}>
                            {/* Social icons placeholder */}
                            <Star size={20} color="var(--text-dim)" />
                            <Smartphone size={20} color="var(--text-dim)" />
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}
